import React, { useState, useEffect } from 'react';

interface BetaSignup {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  heard_on: string | null;
  referral_source: string;
  signed_up_on: string;
}

interface UserQuestion {
  id: number;
  session_id: string;
  user_question: string;
  agent_response: string | null;
  agent_id: string | null;
  timestamp: string;
}

interface AdminDashboardProps {
  onClose?: () => void;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState<'signups' | 'questions'>('signups');
  const [betaSignups, setBetaSignups] = useState<BetaSignup[]>([]);
  const [userQuestions, setUserQuestions] = useState<UserQuestion[]>([]);
  const [filteredQuestions, setFilteredQuestions] = useState<UserQuestion[]>([]);
  const [dateFilter, setDateFilter] = useState<'today' | 'week' | 'month' | 'all'>('today');
  const [modeFilter, setModeFilter] = useState<'all' | 'knowledge' | 'open'>('all');
  const [loading, setLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('adminToken'));

  // Always use relative API endpoints for production builds
  const apiBaseUrl = '/api';

  const fetchData = async () => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      if (activeTab === 'signups') {
        const response = await fetch(`${apiBaseUrl}/admin/beta-signups`, { headers });

        // Handle authentication errors
        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('adminToken');
          setToken(null);
          throw new Error('Session expired. Please login again.');
        }

        if (!response.ok) {
          throw new Error('Failed to fetch beta signups');
        }
        const data = await response.json();
        setBetaSignups(data.data?.signups || data.signups || []);
      } else {
        const response = await fetch(`${apiBaseUrl}/admin/user-questions`, { headers });

        // Handle authentication errors
        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('adminToken');
          setToken(null);
          throw new Error('Session expired. Please login again.');
        }

        if (!response.ok) {
          throw new Error('Failed to fetch user questions');
        }
        const data = await response.json();
        setUserQuestions(data.data?.questions || data.questions || []);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch data';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [activeTab, token]);

  useEffect(() => {
    setFilterLoading(true);
    const timer = setTimeout(() => {
      filterQuestions();
      setFilterLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [userQuestions, dateFilter, modeFilter]);

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getModeName = (agentId: string | null) => {
    if (!agentId) return 'Unknown';
    switch (agentId) {
      case 'agent_6301k5dawazefnht8gj9v7c09zn5':
        return 'Knowledge Base';
      case 'agent_9601k5fc9k76etvb6eaed2r44fzk':
        return 'Open Mode';
      default:
        return 'Unknown';
    }
  };

  const filterQuestions = () => {
    let filtered = [...userQuestions];

    // Date filtering
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());

    if (dateFilter !== 'all') {
      filtered = filtered.filter(question => {
        const questionDate = new Date(question.timestamp);
        switch (dateFilter) {
          case 'today':
            return questionDate >= today;
          case 'week':
            return questionDate >= weekAgo;
          case 'month':
            return questionDate >= monthAgo;
          default:
            return true;
        }
      });
    }

    // Mode filtering
    if (modeFilter !== 'all') {
      filtered = filtered.filter(question => {
        const mode = getModeName(question.agent_id);
        switch (modeFilter) {
          case 'knowledge':
            return mode === 'Knowledge Base';
          case 'open':
            return mode === 'Open Mode';
          default:
            return true;
        }
      });
    }

    setFilteredQuestions(filtered);
  };

  const downloadCSV = () => {
    try {
      // Generate CSV from frontend data
      const headers = ['ID', 'First Name', 'Last Name', 'Email', 'Phone', 'Heard On', 'Source', 'Signed Up On'];
      const csvLines = [headers.join(',')];

      // Add data rows from betaSignups array
      betaSignups.forEach((signup) => {
        const row = [
          signup.id || '',
          (signup.first_name || '').replace(/"/g, '""'),
          (signup.last_name || '').replace(/"/g, '""'),
          (signup.email || '').replace(/"/g, '""'),
          (signup.phone || '').replace(/"/g, '""'),
          (signup.heard_on || '').replace(/"/g, '""'),
          (signup.referral_source || 'Hub Central POC').replace(/"/g, '""'),
          signup.signed_up_on ? new Date(signup.signed_up_on).toLocaleDateString() : ''
        ];

        // Quote fields that contain commas
        const quotedRow = row.map(field =>
          field.toString().includes(',') ? `"${field}"` : field
        );

        csvLines.push(quotedRow.join(','));
      });

      const csvContent = csvLines.join('\n');

      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `beta-signups-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log(`✅ CSV generated with ${betaSignups.length} signups`);
    } catch (err) {
      console.error('❌ CSV generation error:', err);
      setError('Failed to generate CSV');
    }
  };

  const downloadQuestionsCSV = () => {
    try {
      // Filter questions based on current filters
      let filteredQuestions = [...userQuestions];

      // Apply date filter
      if (dateFilter !== 'all') {
        const now = new Date();
        let filterDate: Date;

        switch (dateFilter) {
          case 'today':
            filterDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            break;
          case 'week':
            filterDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            break;
          case 'month':
            filterDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            break;
          default:
            filterDate = new Date(0);
        }

        filteredQuestions = filteredQuestions.filter(question =>
          new Date(question.timestamp) >= filterDate
        );
      }

      // Apply mode filter (if applicable)
      if (modeFilter !== 'all' && modeFilter) {
        filteredQuestions = filteredQuestions.filter(question =>
          question.agent_id === modeFilter
        );
      }

      // Generate CSV from frontend data
      const headers = ['ID', 'Session ID', 'User Question', 'Agent Response', 'Agent ID', 'Timestamp', 'IP Address', 'User Agent'];
      const csvLines = [headers.join(',')];

      filteredQuestions.forEach((question) => {
        const quotedRow = [
          question.id || '',
          `"${(question.session_id || '').replace(/"/g, '""')}"`,
          `"${(question.user_question || '').replace(/"/g, '""')}"`,
          `"${(question.agent_response || '').replace(/"/g, '""')}"`,
          question.agent_id || '',
          question.timestamp ? new Date(question.timestamp).toISOString() : '',
          '', // IP Address - not available in current data structure
          '' // User Agent - not available in current data structure
        ];
        csvLines.push(quotedRow.join(','));
      });

      const csvContent = csvLines.join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `user-questions-${dateFilter}-${modeFilter}-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log(`✅ CSV generated with ${filteredQuestions.length} questions`);
    } catch (err) {
      console.error('❌ CSV generation error:', err);
      setError('Failed to generate CSV');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setToken(null);
    onClose?.();
  };

  if (!token) {
    return <AdminLogin onLogin={setToken} />;
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 3000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '15px',
        width: '90%',
        maxWidth: '1200px',
        height: '90%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid #eee',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ margin: 0, color: '#333' }}>Admin Dashboard</h2>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => window.open('/', '_blank')}
              style={{
                background: '#4ecdc4',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '5px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '14px'
              }}
            >
              🏠 Go to The Hub Central
            </button>
            <button
              onClick={() => window.open('/?mode=open', '_blank')}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '5px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '14px',
                fontWeight: '500'
              }}
              onMouseOver={(e) => {
                (e.target as HTMLButtonElement).style.opacity = '0.9';
              }}
              onMouseOut={(e) => {
                (e.target as HTMLButtonElement).style.opacity = '1';
              }}
            >
              🎨 Open in Open Mode
            </button>
            <button
              onClick={handleLogout}
              style={{
                background: '#ff6b6b',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Logout
            </button>
            {onClose && (
              <button
                onClick={onClose}
                style={{
                  background: '#ccc',
                  color: '#333',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Close
              </button>
            )}
          </div>
        </div>

        {/* Tabs and Actions */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #eee',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'flex' }}>
            <button
              onClick={() => setActiveTab('signups')}
              style={{
                padding: '15px 30px',
                border: 'none',
                background: activeTab === 'signups' ? '#4ecdc4' : 'transparent',
                color: activeTab === 'signups' ? 'white' : '#666',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: activeTab === 'signups' ? 'bold' : 'normal'
              }}
            >
              Beta Signups
            </button>
            <button
              onClick={() => setActiveTab('questions')}
              style={{
                padding: '15px 30px',
                border: 'none',
                background: activeTab === 'questions' ? '#4ecdc4' : 'transparent',
                color: activeTab === 'questions' ? 'white' : '#666',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: activeTab === 'questions' ? 'bold' : 'normal'
              }}
            >
              User Questions
            </button>
          </div>

          {activeTab === 'signups' && (
            <button
              onClick={downloadCSV}
              style={{
                padding: '10px 20px',
                marginRight: '20px',
                background: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              📁 Download CSV
            </button>
          )}

          {activeTab === 'questions' && (
            <button
              onClick={downloadQuestionsCSV}
              style={{
                padding: '10px 20px',
                marginRight: '20px',
                background: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              📁 Download CSV
            </button>
          )}
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          padding: '20px',
          overflow: 'auto'
        }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
              Loading...
            </div>
          )}

          {error && (
            <div style={{
              background: '#fee',
              color: '#c33',
              padding: '15px',
              borderRadius: '5px',
              marginBottom: '20px'
            }}>
              {error}
            </div>
          )}

          {!loading && !error && activeTab === 'signups' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: 0, color: '#333' }}>Beta Signups</h3>
                <span style={{ color: '#666', fontSize: '14px', fontWeight: 'normal' }}>Total: {betaSignups.length}</span>
              </div>
              {betaSignups.length === 0 ? (
                <p style={{ color: '#666' }}>No beta signups yet.</p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f5f5f5' }}>
                        <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>ID</th>
                        <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Name</th>
                        <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Email</th>
                        <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Phone</th>
                        <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Heard On</th>
                        <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Source</th>
                        <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Signed Up</th>
                      </tr>
                    </thead>
                    <tbody>
                      {betaSignups.map((signup) => (
                        <tr key={signup.id}>
                          <td style={{ padding: '12px', border: '1px solid #ddd' }}>{signup.id}</td>
                          <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                            {signup.first_name} {signup.last_name}
                          </td>
                          <td style={{ padding: '12px', border: '1px solid #ddd' }}>{signup.email}</td>
                          <td style={{ padding: '12px', border: '1px solid #ddd' }}>{signup.phone || 'N/A'}</td>
                          <td style={{ padding: '12px', border: '1px solid #ddd' }}>{signup.heard_on || 'N/A'}</td>
                          <td style={{
                            padding: '12px',
                            border: '1px solid #ddd',
                            backgroundColor: signup.referral_source === 'Hub Central POC' ? '#e6f3ff' : '#f0f8e6',
                            fontWeight: '500'
                          }}>
                            {signup.referral_source || 'Hub Central POC'}
                          </td>
                          <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                            {formatDate(signup.signed_up_on)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {!loading && !error && activeTab === 'questions' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: 0, color: '#333' }}>User Questions</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {filterLoading && <span style={{ color: '#4ecdc4', fontSize: '12px' }}>🔄 Updating...</span>}
                  <span style={{ color: '#666', fontSize: '14px', fontWeight: 'normal' }}>Showing: {filteredQuestions.length}</span>
                </div>
              </div>

              {/* Filter Controls */}
              <div style={{
                marginBottom: '20px',
                display: 'flex',
                gap: '15px',
                alignItems: 'center',
                flexWrap: 'wrap'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '5px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    color: '#333'
                  }}>
                    Date Filter:
                  </label>
                  <select
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value as 'today' | 'week' | 'month' | 'all')}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px',
                      backgroundColor: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="today">Today</option>
                    <option value="week">Last Week</option>
                    <option value="month">This Month</option>
                    <option value="all">All</option>
                  </select>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '5px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    color: '#333'
                  }}>
                    Mode Filter:
                  </label>
                  <select
                    value={modeFilter}
                    onChange={(e) => setModeFilter(e.target.value as 'all' | 'knowledge' | 'open')}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px',
                      backgroundColor: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="all">All Modes</option>
                    <option value="knowledge">Knowledge Base</option>
                    <option value="open">Open Mode</option>
                  </select>
                </div>
              </div>

              {filteredQuestions.length === 0 ? (
                <p style={{ color: '#666' }}>No user questions found for the selected filters.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  {filteredQuestions.map((question) => (
                    <div
                      key={question.id}
                      style={{
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        padding: '15px',
                        backgroundColor: '#f9f9f9'
                      }}
                    >
                      <div style={{ marginBottom: '10px' }}>
                        <strong style={{ color: '#333' }}>Session:</strong> {question.session_id} |{' '}
                        <strong style={{ color: '#333' }}>Mode:</strong> {getModeName(question.agent_id)} |{' '}
                        <strong style={{ color: '#333' }}>Time:</strong> {formatDate(question.timestamp)}
                      </div>
                      <div style={{ marginBottom: '10px' }}>
                        <strong style={{ color: '#2c5aa0' }}>Question:</strong>
                        <div style={{
                          background: 'white',
                          padding: '10px',
                          borderRadius: '5px',
                          marginTop: '5px',
                          border: '1px solid #e0e0e0'
                        }}>
                          {question.user_question}
                        </div>
                      </div>
                      {question.agent_response && (
                        <div>
                          <strong style={{ color: '#4ecdc4' }}>Response:</strong>
                          <div style={{
                            background: 'white',
                            padding: '10px',
                            borderRadius: '5px',
                            marginTop: '5px',
                            border: '1px solid #e0e0e0'
                          }}>
                            {question.agent_response}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface AdminLoginProps {
  onLogin: (token: string) => void;
}

const AdminLogin: React.FC<AdminLoginProps> = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apiBaseUrl = '/api';

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${apiBaseUrl}/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      localStorage.setItem('adminToken', data.token);
      onLogin(data.token);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 3000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '40px',
        borderRadius: '15px',
        width: '100%',
        maxWidth: '400px'
      }}>
        <h2 style={{ textAlign: 'center', marginBottom: '30px', color: '#333' }}>
          Admin Login
        </h2>

        {error && (
          <div style={{
            background: '#fee',
            color: '#c33',
            padding: '15px',
            borderRadius: '5px',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleLogin}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '5px',
              color: '#333',
              fontWeight: 'bold'
            }}>
              Username
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '5px',
                border: '1px solid #ddd',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
              placeholder="Enter admin username"
            />
          </div>

          <div style={{ marginBottom: '30px' }}>
            <label style={{
              display: 'block',
              marginBottom: '5px',
              color: '#333',
              fontWeight: 'bold'
            }}>
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '5px',
                border: '1px solid #ddd',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
              placeholder="Enter admin password"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '15px',
              backgroundColor: '#4ecdc4',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1
            }}
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminDashboard;